import { useState, useRef, useEffect } from 'react'
import { X, Shield, AlertTriangle, Lock, Download, Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { authAPI, SESSION_PIN_NOT_CONFIGURED } from '../services/api'
import ModalPortal from './ModalPortal'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

export default function HIPAADisclaimerModal({ isOpen, onClose, onConfirm }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [step, setStep] = useState('disclaimer') // 'disclaimer' or 'pin'
  const [pin, setPin] = useState(['', '', '', ''])
  const [error, setError] = useState(null)
  // ★★★ A STATE OF ITS OWN, NOT A MESSAGE. See handleVerifyPin.
  const [pinNotConfigured, setPinNotConfigured] = useState(false)
  const [loading, setLoading] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const pinRefs = useRef([])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('disclaimer')
      setPin(['', '', '', ''])
      setError(null)
      setPinNotConfigured(false)
      setLoading(false)
      setAcknowledged(false)
    }
  }, [isOpen])

  // Focus first PIN input when entering PIN step
  useEffect(() => {
    if (step === 'pin' && !pinNotConfigured) {
      setTimeout(() => pinRefs.current[0]?.focus(), 100)
    }
  }, [step, pinNotConfigured])

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return
    
    const newPin = [...pin]
    newPin[index] = value.slice(-1)
    setPin(newPin)
    setError(null)
    
    if (value && index < 3) {
      pinRefs.current[index + 1]?.focus()
    } else if (value && index === 3) {
      // Auto-verify when PIN complete
      setTimeout(() => handleVerifyPin(newPin.join('')), 200)
    }
  }

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs.current[index - 1]?.focus()
    }
  }

  const handleContinueToPin = () => {
    if (!acknowledged) {
      setError(t('hipaaDisclaimer.mustAcknowledge'))
      return
    }
    setStep('pin')
  }

  // ★★★ TEST 1.20 — THIS USED TO CALL authAPI.verifyPin, WHICH POSTED TO A ROUTE THAT
  //   HAS NEVER EXISTED ('/auth/verify-pin' — 404 in production 2026-09-22, 0 occurrences
  //   in orella-api's api/index.py). Every patient who typed their CORRECT PIN here was
  //   told "Verification failed", and the data export and the medical-note download sit
  //   behind this modal. It now calls the SHARED caller for the route that actually holds
  //   the PIN the patient set — the same authAPI.verifySessionPin that
  //   PatientSessionLockScreen uses. There is no second copy of that call in this file.
  //
  // ★★★ NO FALLBACK, AND NOT BY OVERSIGHT. The catch that used to sit in verifyPin
  //   accepted ANY 4-digit PIN whenever the server errored — a plain 500 opened this gate
  //   with 0000. A verification we could not perform is NOT a verification that passed, so
  //   the catch below sets a message and NOTHING else. onConfirm() is reachable from
  //   exactly one place: a 2xx whose body says valid.
  //
  // ★★ THE THREE OUTCOMES ARE THREE, NOT TWO.
  //   · 200 {valid:true}   → open the gate.
  //   · 200 {valid:false}  → wrong PIN. The route does NOT throw for this one.
  //   · 400 'Session PIN not configured' → the patient never set a session PIN at all.
  //     That is not a failed verification and must not wear its words. It gets its own
  //     state and its own panel, naming what to do and where. It does NOT let them
  //     through: whether a HIPAA right of access may sit behind an OPTIONAL PIN at all is
  //     Alex's decision and is being put to him separately. This only stops the app
  //     lying about which of the three happened.
  const handleVerifyPin = async (pinCode) => {
    setLoading(true)
    setError(null)
    
    try {
      const result = await authAPI.verifySessionPin(pinCode)
      
      if (result.valid) {
        onConfirm()
        onClose()
      } else {
        setError(t('hipaaDisclaimer.invalidPin'))
        setPin(['', '', '', ''])
        pinRefs.current[0]?.focus()
      }
    } catch (err) {
      if (err?.status === 400 && err?.error === SESSION_PIN_NOT_CONFIGURED) {
        setPinNotConfigured(true)
        setPin(['', '', '', ''])
      } else {
        setError(t('hipaaDisclaimer.verificationFailed'))
        setPin(['', '', '', ''])
        pinRefs.current[0]?.focus()
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-orella-600 to-orella-500 text-white p-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield size={24} />
                <h2 className="text-lg font-bold">{t('hipaaDisclaimer.title')}</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {pinNotConfigured ? (
            /* ★★★ THE NOT-CONFIGURED PANEL — ITS OWN STATE, ITS OWN WORDS, ITS OWN WAY OUT.
               Rendered FIRST so it cannot be reached through the PIN step's inputs: a
               patient with no session PIN has nothing to type, and leaving the boxes on
               screen under a different sentence would invite them to keep guessing.
               It shares NO string with the failure path — see hipaaDisclaimer.pinNotSet*
               against hipaaDisclaimer.verificationFailed. */
            <div className="p-5 space-y-5">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-orella-100 rounded-full flex items-center justify-center">
                  <Lock size={32} className="text-orella-600" />
                </div>
              </div>

              <div className="text-center">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('hipaaDisclaimer.pinNotSetTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('hipaaDisclaimer.pinNotSetDesc')}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    onClose()
                    navigate('/settings/session-pin')
                  }}
                  className="flex-1 py-3 bg-orella-500 text-white font-semibold rounded-full hover:bg-orella-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Settings size={18} />
                  {t('hipaaDisclaimer.pinNotSetAction')}
                </button>
              </div>
            </div>
          ) : step === 'disclaimer' ? (
            <div className="p-5 space-y-4">
              {/* Warning Icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle size={32} className="text-amber-600" />
                </div>
              </div>

              {/* Main Message */}
              <div className="text-center">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('hipaaDisclaimer.importantNotice')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('hipaaDisclaimer.downloadWarning')}
                </p>
              </div>

              {/* Disclaimer Points */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orella-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-orella-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t('hipaaDisclaimer.point1Title')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('hipaaDisclaimer.point1Desc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orella-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-orella-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t('hipaaDisclaimer.point2Title')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('hipaaDisclaimer.point2Desc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orella-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-orella-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t('hipaaDisclaimer.point3Title')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t('hipaaDisclaimer.point3Desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Liability Notice */}
              <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700">
                      {t('hipaaDisclaimer.liabilityTitle')}
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      {t('hipaaDisclaimer.liabilityDesc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Acknowledgment Checkbox */}
              <label className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-orella-300 transition-colors">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => {
                    setAcknowledged(e.target.checked)
                    setError(null)
                  }}
                  className="mt-1 w-5 h-5 text-orella-500 rounded border-gray-300 focus:ring-orella-500"
                />
                <span className="text-sm text-gray-700">
                  {t('hipaaDisclaimer.acknowledgment')}
                </span>
              </label>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleContinueToPin}
                  disabled={!acknowledged}
                  className="flex-1 py-3 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orella-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Lock size={18} />
                  {t('hipaaDisclaimer.enterPin')}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* PIN Entry Header */}
              <div className="text-center">
                <div className="w-16 h-16 bg-orella-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} className="text-orella-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t('hipaaDisclaimer.verifyIdentity')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('hipaaDisclaimer.enterPinToDownload')}
                </p>
              </div>

              {/* PIN Input */}
              <div className="flex justify-center gap-3">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => pinRefs.current[index] = el}
                    type="password"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handlePinChange(index, e.target.value)}
                    onKeyDown={e => handlePinKeyDown(index, e)}
                    disabled={loading}
                    className="w-14 h-16 text-center text-2xl font-semibold border-2 border-gray-200 rounded-xl focus:border-orella-500 focus:outline-none transition-colors disabled:bg-gray-100"
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setStep('disclaimer')
                    setPin(['', '', '', ''])
                    setError(null)
                  }}
                  disabled={loading}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={() => handleVerifyPin(pin.join(''))}
                  disabled={loading || pin.some(d => !d)}
                  className="flex-1 py-3 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 hover:bg-orella-600 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <OrellaSpinner size={20} tone="inherit" />
                      {t('hipaaDisclaimer.verifying')}
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      {t('hipaaDisclaimer.downloadNote')}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  )
}
